import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, Crown } from "lucide-react";

export default function WinProbabilityChart({ probabilityData }) {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    if (probabilityData?.length > 0) {
      const timer = setTimeout(() => setAnimated(true), 100);
      return () => clearTimeout(timer);
    }
  }, [probabilityData]);

  if (!probabilityData || probabilityData.length === 0) return null;

  const maxProb = Math.max(...probabilityData.map((d) => d.probability));
  const sorted = [...probabilityData].sort((a, b) => b.probability - a.probability);
  const favorite = sorted[0];
  const diff = sorted.length >= 2 ? (sorted[0].probability - sorted[sorted.length - 1].probability).toFixed(1) : 0;

  return (
    <Card className="bg-card border-border/50 shadow-xl overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-bold tracking-tight flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          Probabilidade de Vitoria
        </CardTitle>
        {diff > 0 && (
          <p className="text-xs text-muted-foreground mt-1">
            Diferenca de {diff}% entre favorito e mais fraco
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {probabilityData.map((team) => {
          const isFavorite = team.name === favorite.name;
          const barWidth = animated ? (team.probability / 100) * 100 : 0;

          return (
            <div key={team.name} className="space-y-2" data-testid={`prob-team-${team.name}`}>
              {/* Label row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {isFavorite && (
                    <Crown className="h-4 w-4 text-yellow-400 animate-pulse" />
                  )}
                  <span className={`text-sm font-semibold ${isFavorite ? "text-foreground" : "text-muted-foreground"}`}>
                    {team.name}
                  </span>
                </div>
                <span
                  className="text-xl font-black tabular-nums"
                  style={{
                    color: team.fill,
                    fontFamily: "'Barlow Condensed', sans-serif",
                    textShadow: isFavorite ? `0 0 20px ${team.fill}40` : "none",
                  }}
                >
                  {team.probability.toFixed(1)}%
                </span>
              </div>

              {/* Bar */}
              <div className="relative h-3 rounded-full bg-muted/50 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ease-out"
                  style={{
                    width: `${barWidth}%`,
                    background: `linear-gradient(90deg, ${team.fill}cc, ${team.fill})`,
                    boxShadow: isFavorite ? `0 0 12px ${team.fill}60` : "none",
                  }}
                />
                {/* Glow pulse for favorite */}
                {isFavorite && animated && (
                  <div
                    className="absolute inset-y-0 left-0 rounded-full animate-pulse opacity-30"
                    style={{
                      width: `${barWidth}%`,
                      background: team.fill,
                    }}
                  />
                )}
              </div>

              {/* Sub info */}
              <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                <span>Poder: {(team.probability * 0.18).toFixed(1)} pts</span>
                {isFavorite && (
                  <span className="px-1.5 py-0.5 rounded bg-yellow-400/10 text-yellow-400 font-semibold">
                    FAVORITO
                  </span>
                )}
                {team.probability === maxProb && probabilityData.filter(d => d.probability === maxProb).length > 1 && (
                  <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-semibold">
                    EMPATE
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {/* Equilibrium indicator */}
        {parseFloat(diff) < 3 && (
          <div className="mt-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
            <p className="text-xs font-medium text-green-400">
              Times bem equilibrados! Diferenca de apenas {diff}%
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
